/**
 * Data Checks API — the conductor's `/base-check` (read-only) and `/base-check-clean` (the
 * apply).
 *
 * The engine's survey walker replays every respondent of the data through the questionnaire's
 * routing and compares who it says was asked each question against the data on file. The check
 * is ADVISORY and read-only: nothing is written, nothing is cleaned.
 * The endpoint always answers HTTP 200 with the full report shape — `status` carries whether
 * the check ran (see `types/dataChecks.types`).
 *
 * The clean is the other half and the only call here that WRITES. It is advisory about the
 * project in the same way — every outcome about the data is a 200 carrying `status` and
 * `changed` — and reserves two non-200s for requests it refuses outright: 409 (the file is not
 * the one you approved) and 400 (the body is not a request). Both are translated here into
 * errors a caller can branch on without touching axios.
 *
 * Authentication headers are handled automatically by the axios instance.
 */
import { AxiosInstance, isAxiosError } from "axios"

import { resolveAsyncJson } from "api/asyncJob"
import type {
  BaseCheckCleanRequest,
  BaseCheckCleanResult,
  BaseCheckOptions,
  BaseCheckResult,
} from "types/dataChecks.types"

/**
 * Runs the base check for one project and one questionnaire variant.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project to check
 * @param owner - Owner hint (may be "" — resolved server-side for your own projects)
 * @param options - `variant` picks the flow to walk (omit for the questionnaire's own first
 *   variant; the answer echoes which one it walked). `includeHidden` offers HIDDEN questions
 *   to the clean path — it moves the `cleanable` flag and no count.
 * @returns The parsed report. Non-ok statuses are NOT errors here: the server answers 200 with
 *   the complete shape and `status` set, so the caller reads `status` rather than catching.
 * @throws Will throw if the request itself fails, if the body is not valid JSON, or if the
 *   client-side wait cap is exceeded (see below).
 *
 * ASYNC. A full questionnaire over a real data file can outrun the proxy's 120s limit, so the
 * endpoint is async-capable: the server either finishes inside the 10s window and answers 200,
 * or parks the job and answers 202 with a bare job id, which `resolveAsyncJson` then polls to
 * completion. `responseType`/`transformResponse` keep axios from parsing that bare id as JSON.
 *
 * The wait cap in `api/asyncJob.ts` is 20 minutes and it throws LOCALLY — the server keeps
 * working and the finished result stays retrievable by job id. At bdrc scale (635 questions
 * over 510 respondents) a check is ~5s; the cap only matters for enormous trackers, and a
 * timeout there means "we stopped waiting", not "the check failed".
 */
export async function runBaseCheck(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  options?: BaseCheckOptions
): Promise<BaseCheckResult> {
  let url = `/base-check?id=${encodeURIComponent(id)}&hint_owner=${encodeURIComponent(owner)}`
  // One variant per call — an asked condition is a statement about one flow. Omitting the
  // parameter is meaningful (the questionnaire's own first variant), so an empty string is
  // not sent as `variant=`.
  if (options?.variant) {
    url += `&variant=${encodeURIComponent(options.variant)}`
  }
  // A bare flag: the server reads presence-with-no-value as true (`?include_hidden=0` would
  // mean false). Absent is the default, so false is expressed by not sending it.
  if (options?.includeHidden) {
    url += `&include_hidden`
  }

  const response = await axiosInstance.get<string>(url, {
    headers: { "ASync-Request": 10 },
    responseType: "text",
    transformResponse: (data) => data,
  })
  return resolveAsyncJson<BaseCheckResult>(axiosInstance, response, "Base check")
}

/**
 * The data moved since the report the user approved.
 *
 * Thrown for the endpoint's HTTP 409. It is NOT a failure of the clean and must not be
 * presented as one: the file simply is not the one the approved report describes, so the
 * decision that was approved no longer applies to it. The cure is to re-run the check and let
 * the user look again — never an automatic retry, which would apply a decision nobody made
 * about the data as it now stands.
 *
 * `dataHash` is the file's CURRENT stamp, and the server guarantees it is a real hash: the
 * "we could not read the file to find out" case is answered as an advisory `write_failed`
 * instead, precisely so a client can never re-key on an empty string and retry unguarded.
 */
export class BaseCheckCleanStaleError extends Error {
  readonly dataHash: string
  /** The questionnaire's stamp as the clean's own check walked it; "" from an older server. */
  readonly qnrHash: string

  constructor(message: string, dataHash: string, qnrHash = "") {
    super(message)
    this.name = "BaseCheckCleanStaleError"
    this.dataHash = dataHash
    this.qnrHash = qnrHash
  }
}

/**
 * The refusal bodies (`{status, message[, dataHash]}`) arrive as a STRING on the direct
 * response — `responseType: "text"` keeps axios off the 202 job id — and as a parsed object
 * when the job went async and the body came back through `/async-get`, which is an ordinary
 * JSON GET. Both shapes are read here so neither path loses the server's sentence.
 */
const safeParse = (text: string): unknown => {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

const readRefusalBody = (
  data: unknown
): { status?: string; message?: string; dataHash?: string; qnrHash?: string } => {
  const parsed: unknown =
    typeof data === "string" ? safeParse(data) : data
  if (typeof parsed !== "object" || parsed === null) return {}
  const body = parsed as Record<string, unknown>
  return {
    status: typeof body.status === "string" ? body.status : undefined,
    message: typeof body.message === "string" ? body.message : undefined,
    dataHash: typeof body.dataHash === "string" ? body.dataHash : undefined,
    qnrHash: typeof body.qnrHash === "string" ? body.qnrHash : undefined,
  }
}

/**
 * Turns the two refusals into errors the caller can branch on. Anything else is passed
 * through untouched — a transport failure is a transport failure and is not dressed up as a
 * statement about the project.
 */
const translateCleanRefusal = (error: unknown): unknown => {
  if (!isAxiosError(error) || !error.response) return error
  const body = readRefusalBody(error.response.data)

  if (error.response.status === 409) {
    return new BaseCheckCleanStaleError(
      body.message ||
        "This project's data or questionnaire has changed since the report was run, so that report no longer describes it.",
      body.dataHash ?? "",
      body.qnrHash ?? ""
    )
  }
  if (error.response.status === 400) {
    // A caller bug, not a user state: the endpoint reads an absent or unreadable body as a
    // request it must refuse rather than as a request to clean everything. Surfaced with the
    // server's own sentence so it is diagnosable rather than mysterious.
    return new Error(
      body.message ||
        "The clean request was refused as malformed before it reached the project."
    )
  }
  return error
}

/**
 * Applies the clean: blanks the answers the routing says were never asked.
 *
 * THIS WRITES. The endpoint re-runs the base check itself (nothing the caller sends describes
 * the data — the request contributes CONSENT and SCOPE and nothing else), blanks only cells it
 * is certain about, stores a pre-clean copy of the data as a project document, replaces the
 * project's working data file and appends a `data.cleaned` record to the audit chain.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project to clean
 * @param owner - Owner hint (may be "" — resolved server-side for your own projects)
 * @param request - `scope` (REQUIRED — see below), the report's `variant`, and the
 *   `confirmDataHash` of the report being approved.
 * @returns The parsed result. Every outcome ABOUT THE PROJECT is a 200: `changed: true` with a
 *   `clean` summary, or `changed: false` with `diagnostics` saying why nothing was cleaned, or
 *   a failure `status` with `error`. The caller reads `changed` and `status`, never a transport
 *   code, to know what happened.
 * @throws {BaseCheckCleanStaleError} on HTTP 409 — the data moved since the approved report.
 * @throws {Error} on HTTP 400 (a malformed request — a client bug), on a transport failure, on
 *   a body that will not parse, or if the local wait cap is exceeded.
 *
 * TWO GUARDS BEFORE THE REQUEST IS EVEN MADE, both refusing to send something the server would
 * be right to refuse:
 *
 *  - `scope` is required and has no default. It is sent verbatim; an empty list is refused
 *    here rather than becoming a 400 round trip.
 *  - `confirmDataHash` must be a non-empty hash. The endpoint treats an ABSENT hash as "this
 *    caller declined the staleness check" and would apply UNGUARDED — so the one thing this
 *    function must never do is drop an empty one and send the request anyway. It throws
 *    locally instead. (The server refuses a present-but-empty hash for the same reason.)
 *
 * ASYNC, like the check: a clean runs the whole base check, posts the cleaned data back
 * through the engine and makes two repos writes, so it can outlast the proxy's 120s limit. The server either finishes
 * inside the 10s window or parks the job and answers 202 with a bare id, which
 * `resolveAsyncJson` polls out. `/async-get` replays the parked response verbatim — STATUS
 * INCLUDED — so a 409 parked behind a job id still arrives here as a 409, and is translated
 * the same way as one that came back on the submit.
 */
export async function runBaseCheckClean(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  request: BaseCheckCleanRequest
): Promise<BaseCheckCleanResult> {
  if (!request.confirmDataHash) {
    throw new Error(
      "A clean must carry the data stamp of the report it was approved from. Run the base check and apply from its report."
    )
  }
  if (request.confirmQnrHash !== undefined && !request.confirmQnrHash) {
    throw new Error(
      "A clean must carry the questionnaire stamp of the report it was approved from. Run the base check and apply from its report."
    )
  }
  if (Array.isArray(request.scope) && request.scope.length === 0) {
    throw new Error(
      "A clean must name what it applies to: either every eligible question or a list of questions."
    )
  }

  const url = `/base-check-clean?id=${encodeURIComponent(id)}&hint_owner=${encodeURIComponent(owner)}`

  // Built member by member rather than spread, so nothing the caller happens to carry can
  // reach a mutating endpoint's body. `variant` and `includeHidden` are omitted when they
  // carry the server's own default — an absent `variant` means "the questionnaire's own first
  // variant", which is exactly what an empty string means to the server.
  const body: Record<string, unknown> = { scope: request.scope }
  if (request.variant) body.variant = request.variant
  if (request.includeHidden) body.includeHidden = true
  body.confirmDataHash = request.confirmDataHash
  // The routing the user approved: the server refuses (409) when the questionnaire was saved
  // since the report. Absent means "not checked" to the server, so it is sent whenever the
  // report carries one.
  if (request.confirmQnrHash) body.confirmQnrHash = request.confirmQnrHash

  try {
    const response = await axiosInstance.post<string>(url, body, {
      headers: { "ASync-Request": 10 },
      responseType: "text",
      transformResponse: (data) => data,
    })
    return await resolveAsyncJson<BaseCheckCleanResult>(
      axiosInstance,
      response,
      "Clean"
    )
  } catch (error) {
    throw translateCleanRefusal(error)
  }
}
